import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { Loader2, AlertCircle } from "lucide-react";

/**
 * AuthCallback — lands here after Emergent Auth redirects the user back with
 * `#session_id=xxx` in the URL fragment. We exchange the session_id with our
 * backend which verifies the Google identity via Emergent, checks the email
 * against our whitelist, and hands back an app JWT.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  // useRef so StrictMode's double-mount doesn't fire the exchange twice.
  const processed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = location.hash || window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : "";
    if (!sessionId) {
      setError("Missing sign-in session. Please try again.");
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/google/verify", { session_id: sessionId });
        if (data?.token) {
          localStorage.setItem("jb_token", data.token);
          // Strip the session_id fragment before landing on the app.
          window.history.replaceState(null, "", "/");
          window.location.replace("/");
        } else {
          setError("Google sign-in did not return a valid session. Please try again.");
        }
      } catch (err) {
        setError(apiError(err.response?.data?.detail) || "Google sign-in failed. Please try again.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9] px-4" data-testid="auth-callback-error">
        <div className="max-w-md text-center bg-white border border-stone-200 rounded-xl p-8 shadow-sm">
          <AlertCircle className="h-10 w-10 mx-auto text-red-500" />
          <h2 className="font-heading text-2xl font-normal tracking-tight text-stone-900 mt-4">Sign-in blocked</h2>
          <p className="text-sm text-stone-600 mt-3 leading-relaxed" data-testid="auth-callback-error-msg">{error}</p>
          <button
            onClick={() => navigate("/login")}
            data-testid="auth-callback-back"
            className="mt-6 bg-stone-900 hover:bg-stone-800 text-white text-sm px-5 py-2.5 rounded-md transition-colors"
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAF9]" data-testid="auth-callback-loading">
      <div className="text-center">
        <Loader2 className="h-8 w-8 mx-auto text-stone-500 animate-spin" />
        <p className="text-sm text-stone-500 mt-4">Signing you in…</p>
      </div>
    </div>
  );
}
